// Text about "your turn" items written for the AI: refusals the agent fixes
// its input from, and the one-line readings of answers. Japanese or English by
// the owner's locale (`AiLocale`). The screen has its own translated wording.
import type { AiLocale } from "./locale";
import {
  isTurnDraftKind,
  isTurnRequestKind,
  type TurnAnswer,
  type TurnAnswerProblem,
  type TurnDraftSide,
  type TurnDraftTarget,
  type TurnResult,
  type TurnSpec,
} from "./turn";

/** "question", "request" or "draft message" in text for the agent. */
export function turnKindWord(kind: string, locale: AiLocale = "ja"): string {
  if (isTurnDraftKind(kind))
    return locale === "ja" ? "送る文面" : "draft message";
  if (isTurnRequestKind(kind)) return locale === "ja" ? "依頼" : "request";
  return locale === "ja" ? "質問" : "question";
}

const LANG_NAMES: Record<AiLocale, Record<string, string>> = {
  ja: { ja: "日本語", en: "英語", zh: "中国語", ko: "韓国語" },
  en: { ja: "Japanese", en: "English", zh: "Chinese", ko: "Korean" },
};

/** Name of a sending language; uncommon ones stay a code. */
export function turnDraftLangName(
  lang: string,
  locale: AiLocale = "ja",
): string {
  const base = lang.split("-")[0]?.toLowerCase() ?? lang;
  return LANG_NAMES[locale][base] ?? lang;
}

/** Name of the confirmation text's language (the owner's language). */
export function turnDraftSourceLangName(locale: AiLocale = "ja"): string {
  return locale === "ja" ? "日本語" : "English";
}

/** One line saying the sides of a translated draft are out of sync. */
export function turnDraftStaleNote(
  lang: string,
  stale: TurnDraftSide,
  locale: AiLocale = "ja",
): string {
  const send = turnDraftLangName(lang, locale);
  const source = turnDraftSourceLangName(locale);
  if (locale === "ja")
    return stale === "text"
      ? `${source}を直したので、${send}はまだ前の内容です`
      : `${send}を直したので、${source}はまだ前の内容です`;
  return stale === "text"
    ? `The ${source} text was edited, so the ${send} text still has the previous content`
    : `The ${send} text was edited, so the ${source} text still has the previous content`;
}

const SPEC_PROBLEMS = {
  ja: {
    choiceOptions: "choice には options が要ります",
    formFields: "form には fields が要ります",
    requestNoOptions:
      "依頼(check / operate / provide)には options / fields を付けません",
    uiNeedsUi:
      'kind ui には ui(```ui の中身)が要ります。書き方は describe({ guide: "ui/ask" })',
    uiNoOptions:
      "kind ui には options / fields を付けません(選ぶ・埋めるは UI の中に書く)",
    uiOnlyUi: "ui は kind ui の件だけに付けます",
    draftOnly:
      "target / text / subject / lang / source / reply_to / slack_to は送る文面(kind draft)だけの欄です",
    draftNoOptions: "送る文面(draft)には options / fields を付けません",
    draftTarget: "送る文面(draft)には target(slack / email)が要ります",
    draftText: "送る文面(draft)には text(送る本文)が要ります",
    subjectEmail: "subject(件名)はメール(target email)の文面だけに付けます",
    langSource: "lang(送る言語)を付けたら source(確認用の文面)も付けます",
    sourceLang: "source(確認用の文面)は lang(送る言語)を付けたときだけ付けます",
    staleLang: "stale は翻訳のある送る文面だけに付きます",
    replyText: "reply_to には text(相手の元の文)が要ります",
    replyFrom: "reply_to.from(送り主の名前)を付けるなら空にしません",
    replyTranslation: "reply_to.translation(原文の訳)を付けるなら空にしません",
    slackToSlack:
      "slack_to(送り先)は Slack 宛て(target slack)の文面だけに付けます",
    slackToChannel: "slack_to には channel_id(Slack のチャンネル ID)が要ります",
    slackToName: "slack_to.channel_name(呼び名)を付けるなら空にしません",
    slackToThread:
      "slack_to.thread_ts(スレッドの親の ts)を付けるなら空にしません",
  },
  en: {
    choiceOptions: "choice needs options",
    formFields: "form needs fields",
    requestNoOptions:
      "requests (check / operate / provide) take no options / fields",
    uiNeedsUi:
      'kind ui needs ui (the inside of a ```ui fence). See describe({ guide: "ui/ask" })',
    uiNoOptions:
      "kind ui takes no options / fields (write choosing and filling inside the UI)",
    uiOnlyUi: "ui goes only on kind ui items",
    draftOnly:
      "target / text / subject / lang / source / reply_to / slack_to are fields of draft messages (kind draft) only",
    draftNoOptions: "a draft message takes no options / fields",
    draftTarget: "a draft message needs target (slack / email)",
    draftText: "a draft message needs text (the body to send)",
    subjectEmail: "subject goes only on email drafts (target email)",
    langSource:
      "with lang (the sending language), also give source (the confirmation text)",
    sourceLang: "source (the confirmation text) goes only with lang",
    staleLang: "stale exists only on translated draft messages",
    replyText: "reply_to needs text (the original message)",
    replyFrom: "reply_to.from (the sender's name) must not be empty when given",
    replyTranslation:
      "reply_to.translation (translation of the original) must not be empty when given",
    slackToSlack:
      "slack_to (the destination) goes only on Slack drafts (target slack)",
    slackToChannel: "slack_to needs channel_id (the Slack channel id)",
    slackToName: "slack_to.channel_name must not be empty when given",
    slackToThread: "slack_to.thread_ts must not be empty when given",
  },
} as const;

/**
 * What is wrong with the kind/field combination (null when nothing). Drafts
 * are also format-checked (`draftTextProblem`); the confirmation text and the
 * replied-to original are not (they never reach the recipient as written).
 */
export function turnSpecProblem(
  spec: TurnSpec,
  locale: AiLocale = "ja",
): string | null {
  const t = SPEC_PROBLEMS[locale];
  if (spec.kind === "choice" && (spec.options?.length ?? 0) === 0)
    return t.choiceOptions;
  if (spec.kind === "form" && (spec.fields?.length ?? 0) === 0)
    return t.formFields;
  if (isTurnRequestKind(spec.kind) && (spec.options || spec.fields))
    return t.requestNoOptions;
  if (spec.kind === "ui") {
    if ((spec.ui ?? "").trim() === "") return t.uiNeedsUi;
    if (spec.options || spec.fields) return t.uiNoOptions;
  } else if (spec.ui !== undefined) return t.uiOnlyUi;
  if (!isTurnDraftKind(spec.kind)) {
    if (
      spec.target !== undefined ||
      spec.text !== undefined ||
      spec.subject !== undefined ||
      spec.lang !== undefined ||
      spec.source !== undefined ||
      spec.stale !== undefined ||
      spec.reply_to !== undefined ||
      spec.slack_to !== undefined
    )
      return t.draftOnly;
    return null;
  }
  if (spec.options || spec.fields) return t.draftNoOptions;
  if (spec.target === undefined) return t.draftTarget;
  if (spec.text === undefined || spec.text.trim() === "") return t.draftText;
  if (spec.subject !== undefined && spec.target !== "email")
    return t.subjectEmail;
  if (spec.lang !== undefined && (spec.source ?? "").trim() === "")
    return t.langSource;
  if (spec.lang === undefined && spec.source !== undefined) return t.sourceLang;
  if (spec.lang === undefined && spec.stale !== undefined) return t.staleLang;
  // zod's min(1) lets whitespace through.
  if (spec.reply_to !== undefined && spec.reply_to.text.trim() === "")
    return t.replyText;
  if (spec.reply_to?.from !== undefined && spec.reply_to.from.trim() === "")
    return t.replyFrom;
  if (
    spec.reply_to?.translation !== undefined &&
    spec.reply_to.translation.trim() === ""
  )
    return t.replyTranslation;
  if (spec.slack_to !== undefined) {
    if (spec.target !== "slack") return t.slackToSlack;
    if (spec.slack_to.channel_id.trim() === "") return t.slackToChannel;
    if (
      spec.slack_to.channel_name !== undefined &&
      spec.slack_to.channel_name.trim() === ""
    )
      return t.slackToName;
    if (
      spec.slack_to.thread_ts !== undefined &&
      spec.slack_to.thread_ts.trim() === ""
    )
      return t.slackToThread;
  }
  return draftTextProblem(spec.target, spec.text, locale);
}

/** Opening of a code fence; the closing uses the same character, at least as long. */
const FENCE_RE = /^ {0,3}(`{3,}|~{3,})/;
/** Links into trameria. Only person (a mention) may appear in a draft. */
const WIKI_LINK_RE = /\[\[([a-z_]+):[^\]\n]+\]\]/g;
const HEADING_RE = /^ {0,3}#{1,6}(?:[ \t]|$)/;
/** A rule, checked only at a block start (`---` after a paragraph is a setext underline). */
const RULE_RE = /^ {0,3}(?:-{3,}|\*{3,}|_{3,})[ \t]*$/;
/** A table delimiter row (only with `|`). */
const TABLE_DELIMITER_RE =
  /^ {0,3}\|?[ \t]*:?-+:?[ \t]*(?:\|[ \t]*:?-+:?[ \t]*)*\|?[ \t]*$/;

const DRAFT_PROBLEMS = {
  ja: {
    at: (line: number) => `(${line} 行目)`,
    link: "送る文面に trameria へのリンク([[kind:ID|名前]])は書けません。相手は開けないので、名前だけを書くか相手が開ける URL にしてください(人へのメンションの [[person:ID|名前]] だけは書けます)",
    heading:
      "Slack 宛ての文面に見出し(# …)は使えません。太字の行にしてください",
    rule: "Slack 宛ての文面に区切り線(---)は使えません。空行で区切ってください",
    table: "Slack 宛ての文面に表は使えません。箇条書きにしてください",
  },
  en: {
    at: (line: number) => ` (line ${line})`,
    link: "A draft message cannot contain links into trameria ([[kind:ID|name]]); the recipient cannot open them. Write the name only or a URL they can open (only person mentions [[person:ID|name]] are allowed)",
    heading:
      "A Slack draft cannot use headings (# ...). Use a bold line instead",
    rule: "A Slack draft cannot use rules (---). Separate with a blank line",
    table: "A Slack draft cannot use tables. Use a bulleted list",
  },
} as const;

/**
 * Format problems of a draft's body, line by line, skipping code fences. Both
 * targets refuse links into trameria (except person mentions); Slack also
 * refuses headings, tables and rules, which it does not render.
 */
export function draftTextProblem(
  target: TurnDraftTarget,
  text: string,
  locale: AiLocale = "ja",
): string | null {
  const t = DRAFT_PROBLEMS[locale];
  const lines = text.split(/\r?\n/);
  let fence: string | null = null;
  let blockStart = true;
  for (const [i, line] of lines.entries()) {
    const at = t.at(i + 1);
    const open = FENCE_RE.exec(line);
    if (fence !== null) {
      if (
        open &&
        open[1][0] === fence[0] &&
        open[1].length >= fence.length &&
        line.trim() === open[1]
      ) {
        fence = null;
        blockStart = true;
      }
      continue;
    }
    // A ``` line whose rest contains ` is inline code, not a fence (```npm i```).
    if (
      open &&
      !(
        open[1][0] === "`" &&
        line.slice(line.indexOf(open[1]) + open[1].length).includes("`")
      )
    ) {
      fence = open[1];
      continue;
    }
    const atBlockStart = blockStart;
    blockStart = line.trim() === "";
    if ([...line.matchAll(WIKI_LINK_RE)].some((m) => m[1] !== "person"))
      return `${t.link}${at}`;
    if (target !== "slack") continue;
    if (HEADING_RE.test(line)) return `${t.heading}${at}`;
    if (RULE_RE.test(line) && atBlockStart) return `${t.rule}${at}`;
    const next = lines[i + 1];
    if (
      line.includes("|") &&
      next?.includes("|") &&
      TABLE_DELIMITER_RE.test(next)
    )
      return `${t.table}${at}`;
  }
  return null;
}

/** Result words as the agent reads them. */
export const TURN_RESULT_WORD: Record<AiLocale, Record<TurnResult, string>> = {
  ja: {
    ok: "OK",
    fix: "NG",
    done: "完了",
    failed: "問題あり",
    sent: "送った",
    unsent: "送らない",
  },
  en: {
    ok: "OK",
    fix: "NG",
    done: "done",
    failed: "problem",
    sent: "sent",
    unsent: "not sent",
  },
};

const ANSWER_WORDS = {
  ja: {
    attachedNames: (names: string) => `添付 ${names}`,
    attachedCount: (count: number) => `添付 ${count} 件`,
    wroteWithoutChoosing: (text: string) => `選ばずに書いた: ${text}`,
    withComment: (labels: string, text: string) =>
      `${labels} / コメント: ${text}`,
    uiComment: (ui: string, text: string) => `${ui}\n\nコメント: ${text}`,
    yes: "はい",
    no: "いいえ",
    join: (items: string[]) => items.join("、"),
  },
  en: {
    attachedNames: (names: string) => `attached ${names}`,
    attachedCount: (count: number) => `${count} attached`,
    wroteWithoutChoosing: (text: string) => `wrote without choosing: ${text}`,
    withComment: (labels: string, text: string) =>
      `${labels} / comment: ${text}`,
    uiComment: (ui: string, text: string) => `${ui}\n\ncomment: ${text}`,
    yes: "yes",
    no: "no",
    join: (items: string[]) => items.join(", "),
  },
} as const;

/**
 * The answer a ui item's UI sends, with the comment written beside it (the
 * item's free field): the UI's text as it is, then the comment on its own
 * paragraph. No comment (empty or blank): the UI's text alone.
 */
export function uiAnswerText(
  uiText: string,
  comment: string,
  locale: AiLocale = "ja",
): string {
  const text = comment.trim();
  return text === "" ? uiText : ANSWER_WORDS[locale].uiComment(uiText, text);
}

/**
 * One line reading an answer: choices by their labels (with a comment, or
 * "wrote without choosing"), confirm as yes/no, text as is, requests and
 * drafts as the result word and the person's text, plus attached file names.
 */
export function formatTurnAnswer(
  spec: TurnSpec,
  answer: TurnAnswer,
  files?: readonly { name: string }[],
  locale: AiLocale = "ja",
): string {
  const w = ANSWER_WORDS[locale];
  const text = (answer.text ?? "").trim();
  const count = answer.fileIds?.length ?? 0;
  const attached =
    count === 0
      ? ""
      : files && files.length > 0
        ? w.attachedNames(files.map((f) => JSON.stringify(f.name)).join(", "))
        : w.attachedCount(count);
  const withFiles = (s: string) =>
    [s, attached].filter((x) => x !== "").join(" / ");
  if (spec.kind === "choice") {
    const labels = w.join(
      (answer.selected ?? []).map(
        (id) => spec.options?.find((o) => o.id === id)?.label ?? id,
      ),
    );
    if (labels === "")
      return withFiles(text === "" ? "" : w.wroteWithoutChoosing(text));
    return withFiles(text === "" ? labels : w.withComment(labels, text));
  }
  if (spec.kind === "confirm") {
    const word = answer.confirmed ? w.yes : w.no;
    return withFiles(text === "" ? word : w.withComment(word, text));
  }
  // ui keeps the text the UI sent as is.
  if (spec.kind === "text" || spec.kind === "ui")
    return count === 0 ? (answer.text ?? "") : withFiles(text);
  if (
    spec.kind === "check" ||
    spec.kind === "operate" ||
    spec.kind === "draft"
  ) {
    const word = answer.result ? TURN_RESULT_WORD[locale][answer.result] : "";
    return withFiles(text === "" ? word : `${word}: ${text}`);
  }
  if (spec.kind === "provide") return withFiles(text);
  // form: the fields filled, then what was written beside them.
  const filled = w.join(
    Object.entries(answer.fields ?? {}).map(
      ([id, v]) =>
        `${spec.fields?.find((f) => f.id === id)?.label ?? id}: ${v}`,
    ),
  );
  if (text === "") return withFiles(filled);
  return withFiles(filled === "" ? text : w.withComment(filled, text));
}

/**
 * What to add to "sent" for a draft: the full edited text when the person
 * edited it (the agent continues from it), otherwise that it went out as
 * written. Also says when the sent side was stale.
 */
export function draftSentNote(
  spec: TurnSpec,
  answer: TurnAnswer,
  humanEdited: boolean,
  locale: AiLocale = "ja",
): string {
  if (!isTurnDraftKind(spec.kind) || answer.result !== "sent") return "";
  const ja = locale === "ja";
  const sendLang =
    spec.lang !== undefined ? turnDraftLangName(spec.lang, locale) : "";
  const stale =
    spec.lang !== undefined && spec.stale === "text"
      ? ja
        ? `\n(${turnDraftStaleNote(spec.lang, "text", locale)}。送ったのは前の内容の${sendLang})`
        : `\n(${turnDraftStaleNote(spec.lang, "text", locale)}. The ${sendLang} text sent was the previous content)`
      : "";
  if (!humanEdited)
    return ja
      ? `(出した文面のまま送った)${stale}`
      : ` (sent as drafted)${stale}`;
  const subject =
    spec.subject !== undefined && spec.subject !== ""
      ? ja
        ? `件名: ${spec.subject}\n`
        : `Subject: ${spec.subject}\n`
      : "";
  const sourceLang = turnDraftSourceLangName(locale);
  const source =
    spec.lang !== undefined && spec.source !== undefined
      ? ja
        ? `\n確認用の文面(${sourceLang}${spec.stale === "source" ? "。送った文面より前の内容のまま" : ""}):\n${spec.source}`
        : `\nConfirmation text (${sourceLang}${spec.stale === "source" ? ", still the content from before the sent text" : ""}):\n${spec.source}`
      : "";
  return ja
    ? `(本人が直してから送った)${stale}\n本人が直した後の文面:\n${subject}${spec.text ?? ""}${source}`
    : ` (the person edited it before sending)${stale}\nThe text after the person's edit:\n${subject}${spec.text ?? ""}${source}`;
}

/**
 * The text that hands an answer to the agent (as injection or the next run's
 * input).
 */
export function formatTurnAnswerForAgent(
  item: {
    id: string;
    spec: TurnSpec;
    answer: TurnAnswer;
    files?: readonly { name: string }[];
    /** The parent chat's name when a parent agent answered on the person's behalf. */
    parentName?: string | null;
    /** The parent's answer the person replaced. */
    overrides?: TurnAnswer;
    humanEdited?: boolean;
  },
  locale: AiLocale = "ja",
): string {
  const what = turnKindWord(item.spec.kind, locale);
  const answer =
    formatTurnAnswer(item.spec, item.answer, item.files, locale) +
    draftSentNote(item.spec, item.answer, item.humanEdited === true, locale);
  if (locale === "ja") {
    const head = `下の欄の${what}「${item.spec.title}」(id ${item.id})`;
    if (item.overrides !== undefined)
      return `${head}について、本人が上の会話の答え(${formatTurnAnswer(item.spec, item.overrides, undefined, locale)})を答え直した: ${answer}`;
    if (item.parentName !== undefined)
      return `${head}に、親の会話「${item.parentName ?? "無題"}」の Agent が代わりに答えた: ${answer}`;
    return `${head}に本人が欄で答えた: ${answer}`;
  }
  const head = `the ${what} "${item.spec.title}" (id ${item.id}) in the list below`;
  if (item.overrides !== undefined)
    return `The person re-answered ${head}, replacing the parent chat's answer (${formatTurnAnswer(item.spec, item.overrides, undefined, locale)}): ${answer}`;
  if (item.parentName !== undefined)
    return `The agent of the parent chat "${item.parentName ?? "untitled"}" answered ${head} on the person's behalf: ${answer}`;
  return `The person answered ${head}: ${answer}`;
}

/** The answer problem as text for the agent (turn_answer_child). */
export function turnAnswerProblemText(
  p: TurnAnswerProblem,
  locale: AiLocale = "ja",
): string {
  const ja = locale === "ja";
  switch (p.code) {
    case "need_result":
      return ja
        ? `${p.kind} の答えには result(${p.results.join(" / ")})が要ります`
        : `an answer to ${p.kind} needs result (${p.results.join(" / ")})`;
    case "need_fix":
      return ja
        ? "NG(fix)には直す点を text に書きます"
        : "fix needs what to fix in text";
    case "need_failure_reason":
      return ja
        ? "問題あり(failed)には中身を text に書きます"
        : "failed needs what went wrong in text";
    case "need_file_or_text":
      return ja
        ? "provide の答えには添付か text が要ります"
        : "an answer to provide needs a file or text";
    case "draft_need_result":
      return ja
        ? `送る文面の答えには result(${p.results.join(" / ")})が要ります`
        : `an answer to a draft message needs result (${p.results.join(" / ")})`;
    case "sent_no_text":
      return ja
        ? "送った(sent)に text は付けません(文面は件にあります)"
        : "sent takes no text (the text is in the item)";
    case "draft_no_files":
      return ja
        ? "送る文面の答えに添付は付けません"
        : "an answer to a draft message takes no files";
    case "question_no_result":
      return ja
        ? "質問の答えに result は付けません"
        : "an answer to a question takes no result";
    case "need_text":
      return ja ? "答えには text が要ります" : "the answer needs text";
    case "no_files":
      return ja
        ? `${p.kind} の答えに添付は付けません`
        : `an answer to ${p.kind} takes no files`;
  }
}
